import React from "react";
import PatientConfig from "../../../consts/PatientConfig";
import styles from "./Header.module.css";
import CustomButtom from "../../../components/buttons/CustomButton";
import IconWhatsapp from '@mui/icons-material/WhatsApp';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { getPatientAvatar } from '../../../utils/patientAvatarUtils';
import { getInitials } from '../../appointments/v2/utils/appointmentUtils';
import CalendarConfig from "../../../consts/CalendarConfig";
import { SendWhatsAppMessage } from  "../../../api/WhatsappAPI";
import MessagesTemplate from "../../../consts/MessagesTemplate";
import { DataProviderInstance } from "../../../api/DataProvider";
import { formatPhone } from "../../../utils/phoneUtils";
import StatusBadge from "../../patients/components/StatusBadge/StatusBadge";
import AnonymizedBadge from "./AnonymizedBadge";
import PatientOptOutAction from "./PatientOptOutAction";
import DemoTag from "../../../components/demo-data/DemoTag";
import { isDemoRecord } from "../../../components/demo-data/demoData";

/**
 * Idade curta para o cabeçalho: só a maior unidade ("34 anos", "5 meses").
 * Sem data de nascimento (ou data inválida) devolve vazio.
 */
function getShortAge(birthday) {
    if (!birthday) return "";
    try {
        return CalendarConfig.GetAge(birthday).split(", ")[0];
    } catch (error) {
        return "";
    }
}

/**
 * Card de identidade da ficha (cabeçalho, ao lado do resumo): avatar, nome,
 * sexo, idade, telefone, selos de situação e contato rápido (ligar, WhatsApp).
 * As ações de privacidade (LGPD) ficam no card "Privacidade e dados" da seção
 * Dados pessoais; o opt-out de mensagens de relacionamento fica aqui porque é
 * operacional e disponível para quem atende, não só para o proprietário.
 */
function Header(props) {
    const { Patient } = props;
    const { CompanyConfig: companyConfig } = DataProviderInstance();
    const isAnonymized = !!Patient.anonymized_at;

    // "Não informado" (id 99) não vira texto no cabeçalho: só o que está preenchido aparece
    const genderItem = Patient.gender != null && Patient.gender !== "" && String(Patient.gender) !== "99"
        ? PatientConfig.GetGenderById(Patient.gender)
        : null;
    const genderAndAge = [genderItem ? genderItem.Label : "", getShortAge(Patient.birthday)].filter(Boolean).join(", ");
    const phone = !isAnonymized && Patient.phone ? formatPhone(Patient.phone) : "";
    const metaItems = [genderAndAge, phone].filter(Boolean);
    const avatar = getPatientAvatar(Patient.gender, Patient.birthday);

    function OnPhoneClickHandler() {
        window.location.href = `tel:${Patient.phone}`;
    }

    function OnMessageClickHandler() {
        if (!companyConfig) return;

        const templates = MessagesTemplate.getTemplates(companyConfig);
        const currentHour = new Date().getHours();
        let greeting = "Olá";

        if (currentHour >= 5 && currentHour < 12) {
            greeting = "Bom dia";
        } else if (currentHour >= 12 && currentHour < 18) {
            greeting = "Boa tarde";
        } else {
            greeting = "Boa noite";
        }

        let template = templates.PatientGreetingMessage || "{greetings} {name}";
        let message = template;
        message = message.replaceAll("{name}", Patient.name.split(' ')[0]);
        message = message.replaceAll("{greetings}", greeting);
        message = message.replaceAll("{doctor_name}", companyConfig.doctor_name || "");
        message = message.replaceAll("{company_name}", companyConfig.company_name || "");

        SendWhatsAppMessage(Patient.phone, MessagesTemplate.ConvertToWhatsAppText(message), Patient.countryCode);
    }

    return (
        <div className={styles.container} data-tour="patient-record">
            <div className={styles.identity}>
                {avatar ? (
                    <div className={styles.imageContainer}>
                        <img src={avatar} alt="" width="100%" height="100%" />
                    </div>
                ) : (
                    <div className={`${styles.imageContainer} ${styles.initials}`} aria-hidden="true">
                        {getInitials(Patient.name)}
                    </div>
                )}
                <div className={styles.texts}>
                    <span className={styles.eyebrow}>Paciente</span>
                    <h1 className={styles.name}>{Patient.name}</h1>
                    {metaItems.length > 0 && (
                        <div className={styles.meta}>
                            {metaItems.map((item, index) => (
                                <React.Fragment key={item}>
                                    {index > 0 && <span className={styles.metaSeparator} aria-hidden="true">·</span>}
                                    <span>{item}</span>
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                    <div className={styles.badges}>
                        <StatusBadge status={String(Patient.status)} />
                        {isDemoRecord(Patient) && <DemoTag label="Ficha de exemplo" />}
                        {isAnonymized && <AnonymizedBadge anonymizedAt={Patient.anonymized_at} />}
                        {Patient.deleted_at && <AnonymizedBadge anonymizedAt={Patient.deleted_at} label="Excluído em" />}
                    </div>
                </div>
            </div>
            <div className={styles.actions}>
                {phone && (
                    <>
                        <CustomButtom label="Ligar" style={"primary"} variant="outlined" icon={<PhoneOutlinedIcon />} onClick={OnPhoneClickHandler} />
                        <CustomButtom label="WhatsApp" style={"secondary"} variant="contained" icon={<IconWhatsapp />} onClick={OnMessageClickHandler} />
                    </>
                )}
                <PatientOptOutAction patientId={Patient.id} />
            </div>
        </div>
    )
}

export default Header;
